import React, {type ReactNode} from 'react'
import styled from '@emotion/styled'
import {type MealPlan} from '@findhotel/sapi'

import {ButtonLink} from '@vio/atlas/ButtonLink'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Grid} from '@vio/atlas/helpers/Grid'
import {Tag} from '@vio/atlas/Tag'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {Page} from '@vio/core/src/analytics/types/Events'
import {Price as BookingPrice} from '@vio/core/src/api-types/bovio/response/booking'
import {defaultCurrencyCode} from '@vio/core/src/localization/business/currency'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {
  getCancellationDeadline,
  getRefundType
} from '@vio/core/src/offer/business/cancellationPenalties'
import {
  AcceptedPaymentMode,
  getOfferPaymentMode,
  OfferPaymentMode
} from '@vio/core/src/offer/business/offerPaymentMode'
import {CancellationPenalty} from '@vio/core/src/offer/types/offer'
import {OfferPrice} from '@vio/core/src/offer/types/offer'
import {CurrencyDisplayVariant} from '@vio/core/src/price/business/formatPrice'
import {
  getDisplayPrice,
  getPriceCurrencyCode
} from '@vio/core/src/price/business/price'

import {OfferPerks} from '../../accommodation/OfferPerks/OfferPerks'
import {RoomlessDisclaimer} from '../../accommodation/RoomlessDisclaimer'
import {Price} from '../../search/Price'
import {
  RoomDetailsSectionSkeleton,
  RoomPriceSkeleton
} from './RoomDetailsSectionSkeleton'

interface Props {
  /** Simplified version of RoomDetailsSection */
  showRoomDetails?: boolean
  /** Number of nights the room is booked for */
  numberOfNights: number
  /** Name of the room */
  roomName: string
  /** Callback for opening room details overlay    */
  openRoomDetailsOverlay?: () => void
  /** Numbers of rooms */
  numberOfRooms: number
  /** Index of the room */
  roomIndex?: number
  /** SAPI meal plan entries for the booked room */
  mealPlan?: MealPlan[]
  /** Wether it's fetching offer check data */
  isLoading?: boolean
  /** Offer prices */
  offerPrices: OfferPrice[] | BookingPrice[]
  /** Offer cancellation penalties */
  cancellationPenalties?: CancellationPenalty[]
  /** First name of the room's main guest */
  firstName?: string
  /** Last name of the room's main guest */
  lastName?: string
  //** The hotel's time zone */
  hotelTimeZone?: string
  includeNumberOfRooms?: boolean
  /** Indicates if the offer is eligible for Charge Later */
  isChargeLaterEligible?: boolean
  /** Indicates if the user has selected the charge later option in payment page */
  isChargeLaterSelected?: boolean
  /** The page where the component is being rendered at */
  page?: Page
  /**
   * If the rooms should be shown grouped. This way, instead of showing the room index,
   * it will show the total number of rooms, as `2x RoomName`
   */
  groupRoomDetails?: boolean
  /** display divider at the end of section */
  withDivider?: boolean

  currencyDisplayVariant?: CurrencyDisplayVariant

  /**
   * Renders the base rate line inside the room card.
   */
  showBasePrice?: boolean
  /**
   * If true, the room counter will be shown.
   */
  showRoomCounter?: boolean
  /** Slot rendered just above the room price line (e.g. the price-drop notify card) */
  priceTopSlot?: ReactNode
  /** How the guest pays: now, at the property or later */
  paymentMode?: AcceptedPaymentMode | null
  onPerksClick?: () => void
  /** Warns that the booked room is only revealed after booking (roomless offers) */
  showRoomlessDisclaimer?: boolean
  /** Shows "Your room(s)" as the section title, with the room name under it in place of the room counter tag */
  showYourRoomHeading?: boolean
}

interface RoomCounterProps {
  numberOfRooms: number
  roomNumber: number
  groupRoomDetails?: boolean
}

interface PriceComponentProps {
  currencyCode: string
  priceValue: number
  currencyDisplayVariant?: CurrencyDisplayVariant
}

interface RoomPriceProps {
  numberOfNights: Props['numberOfNights']
  isLoading?: Props['isLoading']
  includeNumberOfRooms: Props['includeNumberOfRooms']
  roomPricePerNight: number
  currencyCode: string
  numberOfRooms: number
  totalPricePerRoom: number
  showAverageNightlyPriceText?: boolean
  /**
   * If true, the total price will be the sum of all rooms, not for a single room.
   */
  showPriceOfAllRooms?: boolean
  currencyDisplayVariant?: CurrencyDisplayVariant
}

const NUMBER_OF_DECIMALS_FOR_PRICE = 2

const ButtonLinkWrapper = styled(ButtonLink)`
  text-decoration-color: ${cssTheme.colors.content.neutral
    .secondary} !important;

  &:hover {
    text-decoration-color: ${cssTheme.colors.content.neutral.secondary};
  }
`

const ClickablePerksWrapper = styled.button`
  all: unset;
  cursor: pointer;
  display: block;
  width: 100%;
`

const BasePriceWrapper = styled.div`
  display: inline-block;
`

const RoomNameWrapper = styled.div`
  flex: 1;
  display: flex;
`

const RoomTitleWrapper = styled(Grid)<{isHeadingAbove?: boolean}>`
  padding-bottom: ${({isHeadingAbove}) =>
    isHeadingAbove
      ? cssTheme.layout.spacing.s300
      : cssTheme.layout.spacing.s250};
`
const PriceTextWrapper = styled(Text)`
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  column-gap: ${cssTheme.layout.spacing.s200};
`
const RoomMultiplierWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: ${cssTheme.layout.spacing.s200};
`

const YourRoomsLabel = ({numberOfRooms}: {numberOfRooms: number}) => (
  <FormattedMessageWrapper
    id="room.yourRooms"
    defaultMessage="{numberOfRooms, plural, one {Your room} other {Your rooms}}"
    values={{numberOfRooms}}
  />
)

export const RoomCounter = ({
  numberOfRooms,
  roomNumber,
  groupRoomDetails
}: RoomCounterProps) => (
  <Tag variant="neutral">
    <Text variant="labelXS" colorPath="content.neutral.primary">
      {groupRoomDetails || numberOfRooms == 1 ? (
        <YourRoomsLabel numberOfRooms={numberOfRooms} />
      ) : (
        <FormattedMessageWrapper
          id="room.reference"
          defaultMessage="Room {roomNumber}"
          values={{
            roomNumber
          }}
        />
      )}
    </Text>
  </Tag>
)

const PriceComponent = ({
  priceValue,
  currencyCode,
  currencyDisplayVariant
}: PriceComponentProps) => {
  return (
    <Price
      noStyle
      maximumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
      minimumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
      value={priceValue}
      currency={currencyCode}
      currencyDisplayVariant={currencyDisplayVariant}
    />
  )
}

export const RoomPrice = ({
  isLoading = false,
  roomPricePerNight,
  currencyCode,
  numberOfRooms,
  numberOfNights,
  includeNumberOfRooms,
  totalPricePerRoom,
  showAverageNightlyPriceText = false,
  showPriceOfAllRooms = false,
  currencyDisplayVariant
}: RoomPriceProps) => {
  const totalPrice = showPriceOfAllRooms
    ? totalPricePerRoom * numberOfRooms
    : totalPricePerRoom
  return (
    <Grid container gap="s500" spacing="s000" justify="space-between">
      {isLoading ? (
        <RoomPriceSkeleton />
      ) : (
        <>
          <PriceTextWrapper variant="bodyM" tag="div">
            <BasePriceWrapper>
              <PriceComponent
                priceValue={roomPricePerNight}
                currencyCode={currencyCode}
                currencyDisplayVariant={currencyDisplayVariant}
              />
            </BasePriceWrapper>
            {showAverageNightlyPriceText && (
              <FormattedMessageWrapper
                id="splitBooking.avgNightlyPrice"
                defaultMessage="avg. nightly price"
              />
            )}
            <RoomMultiplierWrapper>
              <FormattedMessageWrapper
                id="payment.priceAndNight"
                defaultMessage="{basePrice} x {nights, plural, one {1 night} other {# nights}}"
                values={{
                  basePrice: '',
                  nights: numberOfNights
                }}
              />
              {numberOfRooms > 1 && includeNumberOfRooms && (
                <FormattedMessageWrapper
                  id="payment.roomsPostFix"
                  defaultMessage="x {rooms, plural, one {1 room} other {# rooms}}"
                  values={{
                    rooms: numberOfRooms
                  }}
                />
              )}
            </RoomMultiplierWrapper>
          </PriceTextWrapper>
          <Text variant="bodyM" tag="div" dataId="total-chargeable-price">
            <PriceComponent
              priceValue={totalPrice}
              currencyCode={currencyCode}
              currencyDisplayVariant={currencyDisplayVariant}
            />
          </Text>
        </>
      )}
    </Grid>
  )
}
export const RoomDetailsSection = ({
  openRoomDetailsOverlay,
  offerPrices,
  cancellationPenalties = [],
  numberOfNights,
  numberOfRooms,
  roomName,
  mealPlan,
  isLoading = false,
  firstName,
  lastName,
  hotelTimeZone,
  roomIndex = 0,
  showRoomDetails = true,
  includeNumberOfRooms = false,
  isChargeLaterEligible,
  isChargeLaterSelected,
  page,
  groupRoomDetails,
  withDivider = true,
  currencyDisplayVariant,
  showBasePrice = false,
  showRoomCounter = true,
  priceTopSlot = null,
  paymentMode,
  onPerksClick,
  showRoomlessDisclaimer = false,
  showYourRoomHeading = false
}: Props) => {
  if (isLoading) return <RoomDetailsSectionSkeleton />

  const displayPrice = getDisplayPrice<OfferPrice | BookingPrice>(offerPrices)

  if (!displayPrice) return null

  const freeCancellationDeadline = getCancellationDeadline(
    cancellationPenalties
  )

  const displayCurrencyCode = getPriceCurrencyCode(displayPrice)
  const currencyCode = displayCurrencyCode || defaultCurrencyCode
  const baseRateDisplayPrice = Number(displayPrice.chargeable.base)
  const roomNumber = roomIndex + 1

  const roomPricePerNightBase =
    baseRateDisplayPrice / numberOfNights / numberOfRooms

  const totalPricePerRoomBase = includeNumberOfRooms
    ? baseRateDisplayPrice
    : baseRateDisplayPrice / numberOfRooms

  const roomTitle = showRoomDetails ? roomName : `${numberOfRooms}x ${roomName}`

  const isRoomCounterVisible =
    showRoomDetails && roomNumber && showRoomCounter && !showYourRoomHeading

  const isMultiRoomBundleVisible = groupRoomDetails && numberOfRooms > 1

  const refundType = getRefundType(cancellationPenalties)
  const paymentType = getOfferPaymentMode({paymentMode})
  const perksPaymentType =
    page === Page.Payment &&
    isChargeLaterEligible &&
    isChargeLaterSelected === false
      ? OfferPaymentMode.PayNow
      : paymentType

  const perks = (
    <OfferPerks
      paymentType={perksPaymentType}
      refundType={refundType}
      cancellationDeadline={freeCancellationDeadline ?? ''}
      timezone={hotelTimeZone}
      mealPlan={mealPlan}
      guestFirstName={firstName}
      guestLastName={lastName}
    />
  )
  return (
    <>
      <Box
        paddingTop="s500"
        paddingBottom={priceTopSlot && !showBasePrice ? 's000' : 's500'}
      >
        {isRoomCounterVisible && (
          <RoomCounter
            numberOfRooms={numberOfRooms}
            roomNumber={roomNumber}
            groupRoomDetails={groupRoomDetails}
          />
        )}
        <Box
          paddingBottom={showRoomDetails && showBasePrice ? 's300' : 's100'}
          paddingTop={isRoomCounterVisible ? 's300' : 's000'}
        >
          {showYourRoomHeading && (
            <Box paddingBottom="s100">
              <Text variant="titleM" dataId="your-room-heading">
                <YourRoomsLabel numberOfRooms={numberOfRooms} />
              </Text>
            </Box>
          )}
          <RoomTitleWrapper
            isHeadingAbove={showYourRoomHeading}
            container
            spacing="s000"
            direction="row"
            justify="space-between"
            alignItems="flex-start"
            gap="s500"
          >
            <RoomNameWrapper>
              {roomName && (
                <Text
                  variant={showYourRoomHeading ? 'bodyM' : 'titleM'}
                  dataId="room-name-title"
                >
                  {isMultiRoomBundleVisible ? (
                    <FormattedMessageWrapper
                      id="bundleRoomName"
                      defaultMessage={'{numberOfRooms}x {roomName}'}
                      values={{numberOfRooms, roomName}}
                    />
                  ) : (
                    roomTitle
                  )}
                </Text>
              )}
            </RoomNameWrapper>
          </RoomTitleWrapper>
          {showRoomlessDisclaimer && (
            <Box paddingBottom="s100">
              <RoomlessDisclaimer />
            </Box>
          )}
          {showRoomDetails &&
            (onPerksClick ? (
              <ClickablePerksWrapper onClick={onPerksClick}>
                {perks}
              </ClickablePerksWrapper>
            ) : (
              perks
            ))}
          {openRoomDetailsOverlay ? (
            <Box paddingTop={showYourRoomHeading ? 's250' : 's200'}>
              <ButtonLinkWrapper
                onClick={openRoomDetailsOverlay}
                size="sm"
                isCompact
                dataId="room-details-button"
              >
                <Text variant="labelS" tag="span">
                  <FormattedMessageWrapper
                    id="moreRoomsDetail"
                    defaultMessage="More details"
                  />
                </Text>
              </ButtonLinkWrapper>
            </Box>
          ) : null}
        </Box>
        {/* The card owns no vertical padding of its own — this wrapper provides
            its gap: 16px above (s400) and 24px below (s600), per design QA.
            The adjacent price padding is zeroed (above/below) only when
            the slot is present, and the outer Box's own paddingBottom is zeroed
            in the FH-MoR case (no price line, card is the last child) — otherwise
            those would stack on top of this wrapper. Bookings without the card
            pass a falsy slot, so all of this collapses and they are unaffected. */}
        {priceTopSlot && (
          <Box paddingTop="s400" paddingBottom="s600">
            {priceTopSlot}
          </Box>
        )}
        {showBasePrice && (
          <Box
            paddingTop={
              priceTopSlot ? 's000' : showRoomDetails ? 's200' : 's000'
            }
          >
            <RoomPrice
              currencyCode={currencyCode}
              numberOfNights={numberOfNights}
              numberOfRooms={numberOfRooms}
              roomPricePerNight={roomPricePerNightBase}
              totalPricePerRoom={totalPricePerRoomBase}
              showPriceOfAllRooms={isMultiRoomBundleVisible}
              includeNumberOfRooms={
                includeNumberOfRooms || isMultiRoomBundleVisible
              }
              isLoading={isLoading}
              currencyDisplayVariant={currencyDisplayVariant}
            />
          </Box>
        )}
      </Box>
      {withDivider && <Divider />}
    </>
  )
}
